import Head from 'next/head';
import { motion } from 'framer-motion';
import { SEOMeta } from '@/templates/SEOMeta';
import { SeoInternalLinks } from '@/components/SeoInternalLinks';
import { useTranslations } from 'next-intl';
import { getBusinessUseCasesI18nProps } from '@/utils/i18n';
import {
    ComputerDesktopIcon,
    CursorArrowRaysIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    GiftIcon,
    MegaphoneIcon,
    TicketIcon,
} from '@heroicons/react/24/outline';

export default function ProgramPromotion({ seoMeta }) {
    const t = useTranslations('businessUseCases');

    const channels = [
        {
            key: 'site',
            icon: ComputerDesktopIcon,
            accent: 'text-blue-700',
            border: 'border-blue-200',
            soft: 'bg-blue-50',
        },
        {
            key: 'social',
            icon: MegaphoneIcon,
            accent: 'text-emerald-700',
            border: 'border-emerald-200',
            soft: 'bg-emerald-50',
        },
        {
            key: 'email',
            icon: EnvelopeIcon,
            accent: 'text-violet-700',
            border: 'border-violet-200',
            soft: 'bg-violet-50',
        },
        {
            key: 'creator',
            icon: TicketIcon,
            accent: 'text-amber-700',
            border: 'border-amber-200',
            soft: 'bg-amber-50',
        },
    ].map((item) => ({
        ...item,
        eyebrow: t(`channels.items.${item.key}.eyebrow`),
        title: t(`channels.items.${item.key}.title`),
        description: t(`channels.items.${item.key}.description`),
        say: t(`channels.items.${item.key}.say`),
        link: t(`channels.items.${item.key}.link`),
        bestFor: t(`channels.items.${item.key}.bestFor`),
        bullets: [
            t(`channels.items.${item.key}.bullets.one`),
            t(`channels.items.${item.key}.bullets.two`),
            t(`channels.items.${item.key}.bullets.three`),
        ],
        sample: {
            label: t(`channels.items.${item.key}.sample.label`),
            title: t(`channels.items.${item.key}.sample.title`),
            body: t(`channels.items.${item.key}.sample.body`),
            cta: t(`channels.items.${item.key}.sample.cta`),
        },
    }));

    const basics = ['one', 'two', 'three'].map((key) => ({
        title: t(`basics.items.${key}.title`),
        description: t(`basics.items.${key}.description`),
    }));

    const collectionJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: t('seo.title'),
        description: t('seo.description'),
        url: 'https://afivio.com/program-promotion',
        mainEntity: {
            '@type': 'ItemList',
            itemListElement: channels.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.title,
                description: item.description,
            })),
        },
    };

    return (
        <>
            <SEOMeta
                title={seoMeta?.title || t('seo.title')}
                description={seoMeta?.description || t('seo.description')}
                keywords={seoMeta?.keywords || t('seo.keywords')}
                url={t('seo.url')}
            />

            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJsonLd) }}
                />
            </Head>

            <div className="bg-white text-slate-900">
                <section className="relative overflow-hidden bg-[linear-gradient(135deg,#f5f9ff_0%,#eef4ff_42%,#f7f2ff_100%)]">
                    <div className="absolute -right-24 top-0 h-80 w-80 rounded-full bg-blue-200/30 blur-3xl" />
                    <div className="absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-violet-200/30 blur-3xl" />

                    <div className="relative mx-auto max-w-7xl px-6 py-20 lg:py-24">
                        <div className="grid gap-14 lg:grid-cols-[minmax(0,0.86fr)_minmax(380px,0.94fr)] lg:items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.45 }}
                                className="max-w-2xl"
                            >
                                <h1 className="text-4xl font-semibold leading-tight text-slate-950 sm:text-5xl">
                                    {t('hero.title')}
                                </h1>

                                <p className="mt-5 max-w-lg text-lg leading-8 text-slate-600">
                                    {t('hero.subtitle')}
                                </p>

                                <div className="mt-8 flex flex-wrap gap-3">
                                    {basics.map((item, index) => (
                                        <motion.div
                                            key={item.title}
                                            initial={{ opacity: 0, y: 14 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.08 + index * 0.06 }}
                                            className="inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm"
                                        >
                                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-950 text-[11px] font-semibold text-white">
                                                {index + 1}
                                            </span>
                                            <span>{item.title}</span>
                                        </motion.div>
                                    ))}
                                </div>

                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.08 }}
                                className="relative"
                            >
                                <div className="relative mx-auto max-w-[560px]">
                                    <div className="absolute left-8 right-8 top-10 h-56 rounded-full bg-blue-200/45 blur-3xl" />
                                    <div className="absolute bottom-8 right-8 h-40 w-40 rounded-full bg-violet-200/45 blur-3xl" />

                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.97, y: 18 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.08 }}
                                        className="relative z-20 rounded-[34px] border border-white/80 bg-white/70 p-3 shadow-[0_36px_120px_rgba(59,130,246,0.20)] backdrop-blur-xl"
                                    >
                                        <div className="rounded-[30px] bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] p-4">
                                            <div className="flex items-center justify-between gap-3 rounded-[22px] border border-slate-200 bg-white px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
                                                    <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                                                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
                                                    <div className="ml-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-500">
                                                        referral-page.example
                                                    </div>
                                                </div>
                                                <div className="hidden items-center gap-2 sm:flex">
                                                    <span className="h-2 w-8 rounded-full bg-blue-500" />
                                                    <span className="h-2 w-12 rounded-full bg-violet-500" />
                                                </div>
                                            </div>

                                            <div className="mt-4 overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(135deg,#f8fbff_0%,#f8fafc_55%,#f5f3ff_100%)] px-5 py-5">
                                                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                                    {t('hero.panel.label')}
                                                </div>

                                                <div className="mt-5 rounded-[24px] border border-slate-200 bg-white p-5">
                                                    <div className="grid grid-cols-4 gap-3">
                                                        {[
                                                            {
                                                                key: channels[0].key,
                                                                label: channels[0].eyebrow,
                                                                icon: ComputerDesktopIcon,
                                                                tone: 'bg-blue-50 text-blue-700 border-blue-200',
                                                            },
                                                            {
                                                                key: channels[1].key,
                                                                label: channels[1].eyebrow,
                                                                icon: MegaphoneIcon,
                                                                tone: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                                                            },
                                                            {
                                                                key: channels[2].key,
                                                                label: channels[2].eyebrow,
                                                                icon: EnvelopeIcon,
                                                                tone: 'bg-violet-50 text-violet-700 border-violet-200',
                                                            },
                                                            {
                                                                key: channels[3].key,
                                                                label: channels[3].eyebrow,
                                                                icon: TicketIcon,
                                                                tone: 'bg-amber-50 text-amber-700 border-amber-200',
                                                            },
                                                        ].map((item, index) => {
                                                            const Icon = item.icon;

                                                            return (
                                                                <motion.div
                                                                    key={item.key}
                                                                    initial={{ opacity: 0, y: 10 }}
                                                                    animate={{ opacity: 1, y: 0 }}
                                                                    transition={{ delay: 0.12 + index * 0.05 }}
                                                                    className="flex flex-col items-center gap-2"
                                                                >
                                                                    <div className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl border ${item.tone}`}>
                                                                        <Icon className="h-6 w-6" />
                                                                    </div>
                                                                    <span className="text-center text-[11px] font-semibold leading-4 text-slate-500">
                                                                        {item.label}
                                                                    </span>
                                                                </motion.div>
                                                            );
                                                        })}
                                                    </div>

                                                    <div className="my-5 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

                                                    <div className="grid grid-cols-3 gap-3">
                                                        {[
                                                            {
                                                                key: 'message',
                                                                label: basics[0].title,
                                                                icon: DocumentTextIcon,
                                                            },
                                                            {
                                                                key: 'reward',
                                                                label: basics[1].title,
                                                                icon: GiftIcon,
                                                            },
                                                            {
                                                                key: 'action',
                                                                label: basics[2].title,
                                                                icon: CursorArrowRaysIcon,
                                                            },
                                                        ].map((item, index) => {
                                                            const Icon = item.icon;

                                                            return (
                                                                <motion.div
                                                                    key={item.key}
                                                                    initial={{ opacity: 0, y: 10 }}
                                                                    animate={{ opacity: 1, y: 0 }}
                                                                    transition={{ delay: 0.2 + index * 0.05 }}
                                                                    className="rounded-[20px] border border-slate-200 bg-slate-50 px-3 py-4 text-center"
                                                                >
                                                                    <div className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm">
                                                                        <Icon className="h-5 w-5" />
                                                                    </div>
                                                                    <div className="mt-3 text-xs font-semibold leading-5 text-slate-700">
                                                                        {item.label}
                                                                    </div>
                                                                </motion.div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                <section className="border-y border-slate-200 bg-white">
                    <div className="mx-auto max-w-7xl px-6 py-5">
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                            <span className="font-semibold text-slate-900">{t('anchors.label')}</span>
                            {channels.map((item) => (
                                <a
                                    key={item.key}
                                    href={`#${item.key}`}
                                    className="text-slate-600 transition-colors hover:text-slate-950"
                                >
                                    {item.eyebrow}
                                </a>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bg-white pb-20">
                    <div className="mx-auto max-w-7xl px-6">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            className="max-w-3xl border-t border-slate-200 pt-16"
                        >
                            <h2 className="text-3xl font-semibold text-slate-950 sm:text-4xl">
                                {t('channels.title')}
                            </h2>
                            <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-600">
                                {t('channels.subtitle')}
                            </p>
                        </motion.div>
                    </div>
                </section>

                {channels.map((item, index) => {
                    const Icon = item.icon;

                    return (
                        <section
                            key={item.key}
                            id={item.key}
                            className={`border-t border-slate-200 py-16 lg:py-20 ${index % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'}`}
                        >
                            <div className="mx-auto max-w-7xl px-6">
                                <div className="grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
                                    <motion.div
                                        initial={{ opacity: 0, y: 18 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.2 }}
                                        className="max-w-xl lg:sticky lg:top-24"
                                    >
                                        <div className="flex items-center gap-4">
                                            <span className={`text-5xl font-semibold leading-none ${item.accent} opacity-35`}>
                                                0{index + 1}
                                            </span>
                                            <div className={`inline-flex items-center gap-3 rounded-full border px-4 py-2 text-sm font-semibold ${item.border} ${item.soft} ${item.accent}`}>
                                                <Icon className="h-5 w-5" />
                                                <span>{item.eyebrow}</span>
                                            </div>
                                        </div>

                                        <h3 className="mt-6 text-3xl font-semibold text-slate-950">
                                            {item.title}
                                        </h3>
                                        <p className="mt-4 text-lg leading-8 text-slate-600">
                                            {item.description}
                                        </p>

                                        <div className="mt-8 space-y-4">
                                            <div className="rounded-[24px] border border-slate-200 bg-white px-5 py-4">
                                                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                                                    {t('channels.labels.say')}
                                                </div>
                                                <p className="mt-3 text-sm leading-7 text-slate-700">
                                                    {item.say}
                                                </p>
                                            </div>

                                            <div className="rounded-[24px] border border-slate-200 bg-white px-5 py-4">
                                                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                                                    {t('channels.labels.link')}
                                                </div>
                                                <p className="mt-3 text-sm leading-7 text-slate-700">
                                                    {item.link}
                                                </p>
                                            </div>

                                            <div className={`rounded-[24px] border px-5 py-4 ${item.border} ${item.soft}`}>
                                                <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${item.accent}`}>
                                                    {t('channels.labels.bestFor')}
                                                </div>
                                                <p className="mt-3 text-sm leading-7 text-slate-700">
                                                    {item.bestFor}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 18 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.2 }}
                                        transition={{ delay: 0.06 }}
                                        className="grid gap-6 xl:grid-cols-[minmax(0,0.92fr)_minmax(280px,0.78fr)]"
                                    >
                                        <div className="rounded-[30px] border border-slate-200 bg-white shadow-sm">
                                            <div className="border-b border-slate-200 px-6 py-5">
                                                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                                    {item.sample.label}
                                                </div>
                                                <div className="mt-2 text-2xl font-semibold text-slate-950">
                                                    {item.sample.title}
                                                </div>
                                            </div>

                                            <div className="px-6 py-6">
                                                <div className="rounded-[24px] bg-slate-50 px-5 py-5 text-sm leading-8 text-slate-700">
                                                    {item.sample.body}
                                                </div>
                                                <div className={`mt-5 inline-flex rounded-full px-4 py-2 text-sm font-semibold ${item.soft} ${item.accent}`}>
                                                    {item.sample.cta}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rounded-[30px] border border-slate-200 bg-white px-6 py-6">
                                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                                {t('channels.labels.checklist')}
                                            </div>
                                            <div className="mt-5 space-y-4">
                                                {item.bullets.map((bullet, bulletIndex) => (
                                                    <motion.div
                                                        key={bullet}
                                                        initial={{ opacity: 0, x: 10 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true, amount: 0.3 }}
                                                        transition={{ delay: bulletIndex * 0.05 }}
                                                        className="flex items-start gap-3"
                                                    >
                                                        <span className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-semibold ${item.soft} ${item.accent}`}>
                                                            {bulletIndex + 1}
                                                        </span>
                                                        <p className="text-sm leading-7 text-slate-700">
                                                            {bullet}
                                                        </p>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </section>
                    );
                })}

                <section className="border-t border-slate-200 bg-white">
                    <SeoInternalLinks className="mx-auto max-w-7xl border-slate-300 px-6 py-10 text-slate-500" />
                </section>
            </div>
        </>
    );
}

export const getStaticProps = getBusinessUseCasesI18nProps;
